/**
 * Calculates the modified Bessel function of the first kind, order 0 (I₀(k)).
 * This is a critical part of the Von Mises PDF normalization constant.
 * We use a series expansion which is accurate for moderate k.
 * I₀(k) = Σ [ (k²/4)^m / (m!)² ] from m=0 to infinity
 * @param x - The input value (kappa).
 * @returns The value of I₀(k).
 */
const besselI0 = (x: number) => {
  let sum = 1.0
  let term = 1.0
  let k = 1
  // The series converges quickly. 20 iterations are more than enough for typical k.
  while (k < 20) {
    term = (term * (x * x)) / (4 * k * k)
    sum += term
    k++
  }
  return sum
}

/**
 * Calculates the Probability Density Function (PDF) of the Von Mises distribution.
 * f(x | μ, κ) = exp(κ * cos(x - μ)) / (2 * π * I₀(κ))
 * @param x - The angle in radians.
 * @param kappa - The concentration parameter (must be > 0).
 * @returns The probability density at angle x.
 */
const vonmisesPDF = (x: number, kappa: number) => {
  const numerator = Math.exp(kappa * Math.cos(x))
  const denominator = 2 * Math.PI * besselI0(kappa)
  return numerator / denominator
}

/**
 * Generates data points for the Von Mises distribution curve from -π to π
 * @param kappa - The concentration parameter.
 * @param loc - The shift location parameter.
 * @param scale - The scale parameter.
 * @param lbound - Lower boundary for data.
 * @param ubound - Upper boundary for data.
 * @param numPoints - The number of points to generate.
 * @returns The labels (x-values) and data (y-values) for the chart.
 */
export const vonmisesSample = (
  kappa: number,
  loc: number,
  scale: number,
  lbound?: number,
  ubound?: number,
  numPoints = 100,
) => {
  const labels = []
  const data = []
  // handle left side -π values
  const stepL = -Math.PI / numPoints
  for (let i = numPoints; i > 0; i--) {
    let x = i * stepL
    const pdf = vonmisesPDF(x, kappa)
    x = x * scale + loc
    if (lbound && x < lbound) continue
    if (ubound && x > ubound) continue
    labels.push(x)
    data.push(pdf / scale)
  }
  // handle right side π values
  const stepR = Math.PI / numPoints
  for (let i = 0; i < numPoints; i++) {
    let x = i * stepR
    const pdf = vonmisesPDF(x, kappa)
    x = x * scale + loc
    if (lbound && x < lbound) continue
    if (ubound && x > ubound) continue
    labels.push(x)
    data.push(pdf / scale)
  }
  return { labels, data }
}
